import { Component, h, State, Host, Event, EventEmitter, Prop, Element } from '@stencil/core';
import { translations } from './translations';
import { sv, uuidv4, awaitTopbar, PnZipCodeSearchLanguages } from '@/index';

/** Check the delivery date for letters, newspapers and direct mail that are distributed every second day. */
@Component({
  tag: 'pn-zipcode-search',
  styleUrl: 'pn-zipcode-search.scss',
})
export class PnZipcodeSearch {
  /** This language property will update when pnTopbar loads */
  searchId: string = `pn-zipcode-${uuidv4()}`;

  @Element() hostElement: HTMLElement;

  @State() validZipCode?: string;
  @State() loading: boolean = false;

  @State() showResult: boolean = false;
  @State() zipCodeSearchResult = {
    delivery: '',
    upcoming: '',
    city: '',
    postalCode: '',
  };

  @State() error: boolean = false;
  @State() errorMessage: keyof typeof translations;

  /** The `language` property will be prioritized before the pnTopbar language. */
  @Prop() language?: PnZipCodeSearchLanguages = null;
  /** Set a pre initialized value. */
  @Prop({ mutable: true }) value?: string;

  /** Event fired when search has result */
  @Event() searchsuccessful: EventEmitter<boolean>;

  async componentWillLoad() {
    if (this.language === null) await awaitTopbar(this.hostElement);
    if (!!this.value) this.handleSearch(this.value);
  }

  private translation(key: keyof typeof translations): string {
    return translations[key][this.language || sv];
  }

  private handleSearch(value: string) {
    this.value = value;
    this.validateZipCode();

    if (this.error) {
      this.errorMessage = 'VALIDATION_ERROR_MESSAGE';
      return;
    }

    this.zipCodeSearch();
  }

  private async zipCodeSearch() {
    this.loading = true;
    await fetch(`https://portal.postnord.com/api/sendoutarrival/closest?postalCode=${this.validZipCode}`)
      .then(response => response.json())
      .then(data => {
        this.showResult = true;
        this.zipCodeSearchResult = data;
        this.searchsuccessful.emit(true);
      })
      .catch(() => {
        this.error = true;
        this.errorMessage = 'SERVER_ERROR_MESSAGE';
        this.searchsuccessful.emit(false);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private validateZipCode(): void {
    // Removes spaces and dashes
    const formatValue = this.value ? this.value.replace(/[ -]/gi, '') : '';

    const validRegex = /^\d{5}$/;
    const valid = validRegex.test(formatValue);

    this.error = !valid;

    if (valid) {
      this.validZipCode = formatValue;
    }
  }

  private renderSearchResult() {
    if (!this.loading && !this.error && this.showResult) {
      return (
        <output class="pn-search-results" htmlFor={this.searchId}>
          <p class="text-row">
            <span>{this.translation('CLOSEST_DELIVERY_DATE')}</span>
            <h3 class="delivery-date">{this.zipCodeSearchResult.delivery}</h3>
          </p>
          <p class="text-row">
            <span>{this.translation('NEXT_DELIVERY_DATE')}</span>
            <h3 class="delivery-date">{this.zipCodeSearchResult.upcoming}</h3>
          </p>
          <p class="text-row">
            <span>{this.translation('ZIP_CODE')}</span>
            <h3>
              {this.zipCodeSearchResult.postalCode}, {this.zipCodeSearchResult.city || 'N/A'}
            </h3>
          </p>
        </output>
      );
    }
  }

  private renderErrorMessage() {
    if (this.error && !this.loading) {
      return (
        <p class="pn-error-text" role="alert">
          <small>{this.translation(this.errorMessage)}</small>
        </p>
      );
    }
  }

  render() {
    return (
      <Host>
        <pn-search-field
          label={this.translation('PLACEHOLDER_TEXT')}
          value={this.value}
          searchid={this.searchId}
          loading={this.loading}
          button="icon"
          buttonLabel={this.translation('SEARCH')}
          placeholder={this.translation('PLACEHOLDER_TEXT')}
          onSearch={e => this.handleSearch(e.detail)}
        />
        {this.renderSearchResult()}
        {this.renderErrorMessage()}
      </Host>
    );
  }
}
